import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, CheckCircle, Building2, Users, Target, Globe, MapPin, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BrandOnboardingProps {
  onComplete: (data: BrandFormData) => void;
  onBack: () => void;
  isCompletion?: boolean;
}

interface BrandFormData {
  companyName: string;
  industry: string;
  description: string;
  website: string;
  headquarters: string;
  foundedYear: string;
  employeeCount: string;
  annualRevenue: string;
  targetMarkets: string[];
  brandValues: string[];
  marketingBudget: string;
  primaryGoals: string[];
  competitorAnalysis: string;
  contactPersonName: string;
  contactPersonTitle: string;
  contactPhone: string;
  socialMedia: {
    twitter: string;
    instagram: string;
    linkedin: string;
    facebook: string;
  };
  marketingObjectives: string;
}

const initialFormData: BrandFormData = {
  companyName: '',
  industry: '',
  description: '',
  website: '',
  headquarters: '',
  foundedYear: '',
  employeeCount: '',
  annualRevenue: '',
  targetMarkets: [],
  brandValues: [],
  marketingBudget: '',
  primaryGoals: [],
  competitorAnalysis: '',
  contactPersonName: '',
  contactPersonTitle: '',
  contactPhone: '',
  socialMedia: {
    twitter: '',
    instagram: '',
    linkedin: '',
    facebook: '',
  },
  marketingObjectives: '',
};

const industries = [
  'Technology', 'Healthcare', 'Finance', 'Retail', 'Manufacturing', 
  'Entertainment', 'Sports & Fitness', 'Food & Beverage', 'Automotive', 
  'Fashion', 'Travel & Tourism', 'Real Estate', 'Education', 'Other'
];

const employeeRanges = [
  '1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'
];

const revenueRanges = [
  'Under $1M', '$1M-$10M', '$10M-$50M', '$50M-$100M', '$100M-$500M', '$500M+'
];

const marketingBudgetRanges = [
  'Under $50K', '$50K-$100K', '$100K-$500K', '$500K-$1M', '$1M-$5M', '$5M+'
];

const steps = [
  { id: 1, title: 'Company Info', icon: Building2 },
  { id: 2, title: 'Business Details', icon: Target },
  { id: 3, title: 'Contact Person', icon: Users },
  { id: 4, title: 'Brand & Marketing', icon: Globe },
  { id: 5, title: 'Review', icon: CheckCircle }
];

export const BrandOnboarding: React.FC<BrandOnboardingProps> = ({
  onComplete,
  onBack,
  isCompletion = false
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<BrandFormData>(initialFormData);
  const [newTargetMarket, setNewTargetMarket] = useState('');
  const [newBrandValue, setNewBrandValue] = useState('');
  const [newGoal, setNewGoal] = useState('');
  const [loading, setLoading] = useState(false);

  const progress = (currentStep / steps.length) * 100;

  const updateFormData = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof BrandFormData] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const addTargetMarket = () => {
    if (newTargetMarket.trim()) {
      updateFormData('targetMarkets', [...formData.targetMarkets, newTargetMarket.trim()]);
      setNewTargetMarket('');
    }
  };

  const removeTargetMarket = (index: number) => {
    updateFormData('targetMarkets', formData.targetMarkets.filter((_, i) => i !== index));
  };

  const addBrandValue = () => {
    if (newBrandValue.trim()) {
      updateFormData('brandValues', [...formData.brandValues, newBrandValue.trim()]);
      setNewBrandValue('');
    }
  };

  const removeBrandValue = (index: number) => {
    updateFormData('brandValues', formData.brandValues.filter((_, i) => i !== index));
  };

  const addGoal = () => {
    if (newGoal.trim()) {
      updateFormData('primaryGoals', [...formData.primaryGoals, newGoal.trim()]);
      setNewGoal('');
    }
  };

  const removeGoal = (index: number) => {
    updateFormData('primaryGoals', formData.primaryGoals.filter((_, i) => i !== index));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in first');
        return;
      }

      // Get or create profile
      let { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile) {
        const { data: newProfile, error: profileError } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            email: user.email || '',
            display_name: formData.companyName || user.email?.split('@')[0] || 'Brand',
            account_type: 'brand',
            setup_completed: true
          })
          .select('id')
          .single();

        if (profileError) {
          console.error('Error creating profile:', profileError);
          toast.error('Failed to create profile. Please try again.');
          return;
        }
        profile = newProfile;
      }

      // Prepare brand data - allow empty values
      const brandData = {
        profile_id: profile.id,
        company_name: formData.companyName || 'Brand',
        industry: formData.industry || '',
        description: formData.description || '',
        website: formData.website || '',
        company_size: formData.employeeCount || '',
        budget_range: formData.marketingBudget || '',
        target_demographics: formData.targetMarkets.length > 0 ? formData.targetMarkets : [],
        campaign_objectives: formData.primaryGoals.length > 0 ? formData.primaryGoals : [],
        contact_person_name: formData.contactPersonName || '',
        contact_person_title: formData.contactPersonTitle || '',
        contact_phone: formData.contactPhone || '',
        headquarters: formData.headquarters || '',
        founded_year: formData.foundedYear ? parseInt(formData.foundedYear) || null : null,
        annual_revenue: formData.annualRevenue || '',
        brand_values: formData.brandValues.length > 0 ? formData.brandValues : [],
        social_media: formData.socialMedia,
        marketing_objectives: formData.marketingObjectives || '',
      };

      // Create brand record
      const { error } = await supabase
        .from('brands')
        .insert(brandData);

      if (error) {
        console.error('Error creating brand:', error);
        toast.error('Failed to create brand profile');
        return;
      }

      // Update profile account_type with email and display_name
      await supabase
        .from('profiles')
        .update({ 
          email: user.email || '',
          display_name: formData.companyName || user.email?.split('@')[0] || 'Brand',
          account_type: 'brand',
          setup_completed: true 
        })
        .eq('id', profile.id);

      toast.success('Brand profile created successfully!');
      onComplete(formData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <Building2 className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Company Information</h3>
              <p className="text-muted-foreground text-sm">
                Tell us about your company and brand
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                placeholder="e.g., Nike, Apple, Microsoft"
                value={formData.companyName}
                onChange={(e) => updateFormData('companyName', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="industry">Industry *</Label>
              <Select value={formData.industry} onValueChange={(value) => updateFormData('industry', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((industry) => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Company Description *</Label>
              <Textarea
                id="description"
                placeholder="Provide a brief description of your company, what you do, and your mission..."
                value={formData.description}
                onChange={(e) => updateFormData('description', e.target.value)}
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                placeholder="https://www.yourcompany.com"
                value={formData.website}
                onChange={(e) => updateFormData('website', e.target.value)}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <Target className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Business Details</h3>
              <p className="text-muted-foreground text-sm">
                Share your company size, revenue, and target markets
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="headquarters">Headquarters</Label>
                <Input
                  id="headquarters"
                  placeholder="e.g., San Francisco, CA"
                  value={formData.headquarters}
                  onChange={(e) => updateFormData('headquarters', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="foundedYear">Founded Year</Label>
                <Input
                  id="foundedYear"
                  type="number"
                  placeholder="e.g., 2010"
                  value={formData.foundedYear}
                  onChange={(e) => updateFormData('foundedYear', e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="employeeCount">Employee Count</Label>
              <Select value={formData.employeeCount} onValueChange={(value) => updateFormData('employeeCount', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee range" />
                </SelectTrigger>
                <SelectContent>
                  {employeeRanges.map((range) => (
                    <SelectItem key={range} value={range}>
                      {range}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="annualRevenue">Annual Revenue</Label>
              <Select value={formData.annualRevenue} onValueChange={(value) => updateFormData('annualRevenue', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select revenue range" />
                </SelectTrigger>
                <SelectContent>
                  {revenueRanges.map((range) => (
                    <SelectItem key={range} value={range}>
                      {range}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Target Markets</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Add the markets or regions your company serves
                </p>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="e.g., North America, Europe, Asia"
                    value={newTargetMarket}
                    onChange={(e) => setNewTargetMarket(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTargetMarket()}
                  />
                  <Button onClick={addTargetMarket} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.targetMarkets.map((market, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeTargetMarket(index)}
                    >
                      {market} ×
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <Users className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Contact Person</h3>
              <p className="text-muted-foreground text-sm">
                Primary contact information for marketing partnerships
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contactPersonName">Contact Person Name</Label>
                <Input
                  id="contactPersonName"
                  placeholder="e.g., John Smith"
                  value={formData.contactPersonName}
                  onChange={(e) => updateFormData('contactPersonName', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contactPersonTitle">Title/Position</Label>
                <Input
                  id="contactPersonTitle"
                  placeholder="e.g., Marketing Director, CMO"
                  value={formData.contactPersonTitle}
                  onChange={(e) => updateFormData('contactPersonTitle', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contactPhone">Phone Number</Label>
                <Input
                  id="contactPhone"
                  placeholder="(555) 123-4567"
                  value={formData.contactPhone}
                  onChange={(e) => updateFormData('contactPhone', e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium">Social Media Profiles</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="twitter">Twitter/X</Label>
                  <Input
                    id="twitter"
                    placeholder="@yourcompany"
                    value={formData.socialMedia.twitter}
                    onChange={(e) => updateFormData('socialMedia.twitter', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="instagram">Instagram</Label>
                  <Input
                    id="instagram"
                    placeholder="@yourcompany"
                    value={formData.socialMedia.instagram}
                    onChange={(e) => updateFormData('socialMedia.instagram', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="linkedin">LinkedIn</Label>
                  <Input
                    id="linkedin"
                    placeholder="Company page URL"
                    value={formData.socialMedia.linkedin}
                    onChange={(e) => updateFormData('socialMedia.linkedin', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="facebook">Facebook</Label>
                  <Input
                    id="facebook"
                    placeholder="@yourcompany"
                    value={formData.socialMedia.facebook}
                    onChange={(e) => updateFormData('socialMedia.facebook', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <Globe className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Brand & Marketing</h3>
              <p className="text-muted-foreground text-sm">
                Share your brand values and marketing objectives
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="marketingBudget">Annual Marketing Budget</Label>
              <Select value={formData.marketingBudget} onValueChange={(value) => updateFormData('marketingBudget', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select budget range" />
                </SelectTrigger>
                <SelectContent>
                  {marketingBudgetRanges.map((range) => (
                    <SelectItem key={range} value={range}>
                      {range}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Brand Values</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Add your core brand values and principles
                </p>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="e.g., Innovation, Sustainability, Excellence"
                    value={newBrandValue}
                    onChange={(e) => setNewBrandValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addBrandValue()}
                  />
                  <Button onClick={addBrandValue} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.brandValues.map((value, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeBrandValue(index)}
                    >
                      {value} ×
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Primary Marketing Goals</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  What are your main marketing objectives?
                </p>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="e.g., Brand Awareness, Lead Generation, Sales"
                    value={newGoal}
                    onChange={(e) => setNewGoal(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addGoal()}
                  />
                  <Button onClick={addGoal} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.primaryGoals.map((goal, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeGoal(index)}
                    >
                      {goal} ×
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="marketingObjectives">Marketing Objectives</Label>
              <Textarea
                id="marketingObjectives"
                placeholder="Describe your main marketing objectives and what you hope to achieve..."
                value={formData.marketingObjectives}
                onChange={(e) => updateFormData('marketingObjectives', e.target.value)}
                rows={4}
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <Building2 className="h-12 w-12 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Review Your Brand Profile</h3>
              <p className="text-muted-foreground">
                Please review all information before creating your brand profile
              </p>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  {formData.companyName || 'Brand Name'}
                </CardTitle>
                <CardDescription>{formData.industry || 'Industry'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.description && (
                  <div>
                    <h4 className="font-medium mb-1">Description</h4>
                    <p className="text-sm text-muted-foreground">{formData.description}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Website:</span>
                    <p className="text-muted-foreground">{formData.website || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Headquarters:</span>
                    <p className="text-muted-foreground">{formData.headquarters || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Employees:</span>
                    <p className="text-muted-foreground">{formData.employeeCount || 'Not specified'}</p>
                  </div>
                  <div>
                    <span className="font-medium">Revenue:</span>
                    <p className="text-muted-foreground">{formData.annualRevenue || 'Not specified'}</p>
                  </div>
                </div>
                
                {formData.brandValues.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Brand Values</h4>
                    <div className="flex flex-wrap gap-1">
                      {formData.brandValues.map((value, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {value}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {formData.primaryGoals.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Marketing Goals</h4>
                    <div className="flex flex-wrap gap-1">
                      {formData.primaryGoals.map((goal, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {goal}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="mx-auto max-w-2xl">
        <Card className="w-full">
          <CardHeader className="text-center space-y-2">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Badge variant="outline">Brand Setup</Badge>
              <div className="w-16" /> {/* Spacer for alignment */}
            </div>
            
            <CardTitle className="text-2xl font-bold">Brand Onboarding</CardTitle>
            <CardDescription>
              Let's set up your brand profile to connect with athletes and influencers
            </CardDescription>
            
            <div className="w-full bg-muted rounded-full h-2 mt-6">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300" 
                style={{ width: `${progress}%` }}
              />
            </div>
            
            <div className="flex justify-center mt-4">
              <div className="flex items-center space-x-4">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = currentStep === step.id;
                  const isCompleted = currentStep > step.id;
                  
                  return (
                    <div key={step.id} className="flex items-center">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors ${
                        isCompleted ? 'bg-primary border-primary text-primary-foreground' :
                        isActive ? 'border-primary text-primary' :
                        'border-muted-foreground text-muted-foreground'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <Icon className="h-4 w-4" />
                        )}
                      </div>
                      {index < steps.length - 1 && (
                        <div className={`w-8 h-0.5 mx-2 ${
                          isCompleted ? 'bg-primary' : 'bg-muted'
                        }`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {renderStepContent()}
            
            <div className="flex justify-between pt-6 border-t">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={currentStep === 1 ? onBack : prevStep}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {currentStep === 1 ? (isCompletion ? 'Back' : 'Back to Roles') : 'Previous'}
                </Button>
                {isCompletion && (
                  <Button 
                    variant="ghost" 
                    onClick={() => window.location.href = '/profile'}
                    className="flex items-center gap-2"
                  >
                    Return to Profile
                  </Button>
                )}
              </div>
              
              {currentStep === steps.length ? (
                <Button onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Creating Profile...' : 'Complete Setup'}
                </Button>
              ) : (
                <Button onClick={nextStep}>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};